import { useEffect, useState } from 'react'

import { t } from './i18n'

/**
 * What a student looks at before their teacher arrives.
 *
 * Being alone in a video call reads as "this is broken" — and for a child on a
 * flaky Cairo connection that is exactly the wrong conclusion, because the next
 * thing they do is close the tab and tell a parent the class did not work. So
 * this says clearly that they ARE in, and looks like something is happening.
 *
 * Everything is CSS: three bouncing dots, a drifting set of shapes, and a
 * rotating line of encouragement. No canvas, no images, no timers driving
 * layout — the phones our students use need their CPU for video decode, and this
 * screen must cost nothing that matters. The one JS timer swaps a string every
 * few seconds, which is a text change, not layout.
 */

const HINT_KEYS = ['waiting.hint1', 'waiting.hint2', 'waiting.hint3', 'waiting.hint4'] as const
const HINT_INTERVAL_MS = 4_500

export function WaitingScene({ teacherView }: { teacherView: boolean }) {
  const [hint, setHint] = useState(0)

  useEffect(() => {
    // Teachers get no rotating chatter — they are working, not waiting.
    if (teacherView) return
    const timer = window.setInterval(() => {
      setHint((current) => (current + 1) % HINT_KEYS.length)
    }, HINT_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [teacherView])

  return (
    <div className="waiting" role="status">
      <div className="waiting__scene" aria-hidden="true">
        {/* Drifting shapes, behind everything: motion in the periphery reads as
            "alive" without pulling the eye away from the message. */}
        <span className="waiting__blob waiting__blob--1" />
        <span className="waiting__blob waiting__blob--2" />
        <span className="waiting__blob waiting__blob--3" />

        <div className="waiting__door">
          <span className="waiting__door-panel" />
          <span className="waiting__door-knob" />
        </div>

        <div className="waiting__dots">
          <i />
          <i />
          <i />
        </div>
      </div>

      <p className="waiting__title">{t(teacherView ? 'room.waitingStudents' : 'waiting.title')}</p>

      {teacherView ? null : (
        <>
          <p className="waiting__sub">{t('waiting.sub')}</p>
          {/* key forces a remount so the entrance animation replays per hint. */}
          <p className="waiting__hint" key={hint}>
            {t(HINT_KEYS[hint])}
          </p>
        </>
      )}
    </div>
  )
}
